import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
	BulkUpdateCurrentLevelsResponseJSON,
	ListAssignmentHistoryResponseJSON,
	ListCompanyCompetencyAssignmentsResponseJSON,
	ListCompetencyAssignmentsResponseJSON,
	ListTeamCompetencyAssignmentsResponseJSON,
	UpdateCurrentLevelResponseJSON,
} from "@performyard/service-contracts";

import {
	bulkUpdateAssignmentCurrentLevels,
	getAssignmentHistory,
	getCompanyCompetencyAssignments,
	getCompetencyAssignments,
	getTeamCompetencyAssignments,
	updateAssignmentCurrentLevel,
} from "../../services/defs/competency-assignments";
import type {
	BulkCurrentLevelUpdate,
	ListAssignmentHistoryParams,
	ListCompanyCompetencyAssignmentsParams,
	ListCompetencyAssignmentsParams,
	ListTeamCompetencyAssignmentsParams,
} from "../../services/defs/competency-assignments";
import type { PYUseMutationOptions, PYUseQueryOptions } from "../StandardQueryArguments";

// Query keys
export const competencyAssignmentsKeys = {
	all: ["CompetencyAssignments"] as const,
	list: (params?: ListCompetencyAssignmentsParams) => [...competencyAssignmentsKeys.all, "list", params] as const,
	team: (params?: ListTeamCompetencyAssignmentsParams) => [...competencyAssignmentsKeys.all, "team", params] as const,
	company: (params?: ListCompanyCompetencyAssignmentsParams) =>
		[...competencyAssignmentsKeys.all, "company", params] as const,
	history: (assignmentId: string, params?: ListAssignmentHistoryParams) =>
		[...competencyAssignmentsKeys.all, "history", assignmentId, params] as const,
};

// List competency assignments
export const useCompetencyAssignments = (
	params: ListCompetencyAssignmentsParams = {},
	options?: PYUseQueryOptions<ListCompetencyAssignmentsResponseJSON>
) => {
	return useQuery({
		queryKey: competencyAssignmentsKeys.list(params),
		queryFn: () => getCompetencyAssignments(params),
		meta: {
			errorMessage: "There was an error getting the competencies.",
		},
		...options,
	});
};

// Competency assignment history
export const useAssignmentHistory = (
	assignmentId: string,
	params: ListAssignmentHistoryParams = {},
	options?: PYUseQueryOptions<ListAssignmentHistoryResponseJSON>
) => {
	return useQuery({
		queryKey: competencyAssignmentsKeys.history(assignmentId, params),
		queryFn: () => getAssignmentHistory(assignmentId, params),
		enabled: !!assignmentId,
		meta: {
			errorMessage: "There was an error getting competency history.",
		},
		...options,
	});
};

// List a manager's direct reports' competency assignments
export const useTeamCompetencyAssignments = (
	params: ListTeamCompetencyAssignmentsParams = {},
	options?: PYUseQueryOptions<ListTeamCompetencyAssignmentsResponseJSON>
) => {
	return useQuery({
		queryKey: competencyAssignmentsKeys.team(params),
		queryFn: () => getTeamCompetencyAssignments(params),
		meta: {
			errorMessage: "There was an error getting team competencies.",
		},
		...options,
	});
};

// Org-wide assignment list for Company Info → Competencies (admin-gated server-side)
export const useCompanyCompetencyAssignments = (
	params: ListCompanyCompetencyAssignmentsParams = {},
	options?: PYUseQueryOptions<ListCompanyCompetencyAssignmentsResponseJSON>
) => {
	return useQuery({
		queryKey: competencyAssignmentsKeys.company(params),
		queryFn: () => getCompanyCompetencyAssignments(params),
		meta: {
			errorMessage: "There was an error getting company competencies.",
		},
		...options,
	});
};

// Update an assignment's current level (manager / chain-of-command / admin)
export const useUpdateAssignmentCurrentLevel = (options?: PYUseMutationOptions<UpdateCurrentLevelResponseJSON>) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ assignmentId, currentLevel }: { assignmentId: string; currentLevel: number | null }) =>
			updateAssignmentCurrentLevel(assignmentId, currentLevel),
		onSuccess: () => {
			// The level (and its level-gap classification) changed and a new history row was written —
			// refetch every assignment list + history view.
			queryClient.invalidateQueries({ queryKey: competencyAssignmentsKeys.all });
		},
		...options,
	});
};

// Bulk current-level update (inline edit mode). A 200 can still carry per-item failures in
// `failed[]` — invalidate unconditionally since any successful item needs the same refetch as the
// single-item mutation above.
export const useBulkUpdateAssignmentCurrentLevels = (
	options?: PYUseMutationOptions<BulkUpdateCurrentLevelsResponseJSON, unknown, BulkCurrentLevelUpdate[]>
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (updates: BulkCurrentLevelUpdate[]) => bulkUpdateAssignmentCurrentLevels(updates),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: competencyAssignmentsKeys.all });
		},
		...options,
	});
};
